import styled from 'styled-components';

import { TableBuilder, type TableBuilderProps } from '../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../cdl/TableBuilder/TableBuilderColumn';
import { Text } from '../../cdl/Text/Text';
import { formatNumber } from '../../common/helpers/formatNumber.helper';
import { translate } from '../../common/helpers/translate.helper';
import { Box } from '../../common/ui/Box';
import type { DemandCategorySummary } from '../types/DemandCategorySummary';
import type { DemandProductCategoryOption } from '../types/DemandProductCategoryOption';

const SummaryTable = styled(Box)<{ $noWrap: boolean }>`
    overflow-x: ${({ $noWrap }) => ($noWrap ? 'clip' : 'visible')};

    th,
    td {
        white-space: ${({ $noWrap }) => ($noWrap ? 'nowrap' : 'normal')};
        overflow-wrap: normal;
    }
`;

interface DemandCategorySummaryTableProps {
    categorySummaries: DemandCategorySummary[];
    categories: DemandProductCategoryOption[];
    totalVolume: number;
    variant?: TableBuilderProps<DemandCategorySummary>['variant'];
}

export const DemandCategorySummaryTable = ({
    categorySummaries,
    categories,
    totalVolume,
    variant = 'default',
}: DemandCategorySummaryTableProps) => {
    const categoryLabels = new Map(categories.map((category) => [category.id, category.label]));
    return (
        <SummaryTable $noWrap={variant === 'inline'}>
            <TableBuilder<DemandCategorySummary> data={categorySummaries} variant={variant}>
                <TableBuilderColumn<DemandCategorySummary>
                    header={translate('demandDefinition.productCategory')}
                    width={variant === 'inline' ? undefined : '48%'}
                >
                    {(category) => categoryLabels.get(category.category) ?? category.category}
                </TableBuilderColumn>
                <TableBuilderColumn<DemandCategorySummary>
                    header={translate('demandDefinition.vessels')}
                    width={variant === 'inline' ? undefined : '16%'}
                    numeric
                >
                    {(category) => formatNumber({ number: category.vesselCount, maximumFractionDigits: 0 })}
                </TableBuilderColumn>
                <TableBuilderColumn<DemandCategorySummary>
                    header={translate('demandDefinition.percentage')}
                    width={variant === 'inline' ? undefined : '12%'}
                    numeric
                >
                    {(category) =>
                        formatNumber({
                            number: category.sharePercent,
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })
                    }
                </TableBuilderColumn>
                <TableBuilderColumn<DemandCategorySummary>
                    header={translate('demandDefinition.volumeLitres')}
                    width={variant === 'inline' ? undefined : '24%'}
                    numeric
                >
                    {(category) =>
                        formatNumber({
                            number: category.volume,
                            maximumFractionDigits: 0,
                        })
                    }
                </TableBuilderColumn>
            </TableBuilder>
            <Box
                display="grid"
                gridTemplateColumns="48% 16% 12% 24%"
                paddingX={variant === 'inline' ? 8 : 16}
                paddingY={8}
                backgroundColor={variant === 'inline' ? undefined : 'background.subtle'}
                border={variant === 'inline' ? 0 : '1px solid'}
                borderTop={0}
                borderColor="background.inset"
            >
                <Text variant="small" fontWeight={600} style={{ gridColumn: '1 / 4' }}>
                    {translate('demandDefinition.total')}
                </Text>
                <Text variant="small" fontWeight={600} textAlign="right">
                    {formatNumber({
                        number: totalVolume,
                        maximumFractionDigits: 0,
                    })}
                </Text>
            </Box>
        </SummaryTable>
    );
};
